import { useEffect, useState, type ReactNode } from 'react';
import {
  MaterialReactTable, useMaterialReactTable, MRT_TopToolbar,
  MRT_ShowHideColumnsButton, MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MRT_ToggleGlobalFilterButton,
  type MRT_ColumnDef, type MRT_ColumnFiltersState, type MRT_PaginationState,
  type MRT_RowSelectionState, type MRT_SortingState,
} from 'material-react-table';
import 'dayjs/locale/en-gb';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Alert, Box, Button, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import colorConfigs from '../../../configs/colorConfigs';
import { tableIconButtonStyle } from '../../../configs/buttonStyles';
import { type ApiError } from '../../../services/apiError';
import TablePaginationControls from './TablePaginationControls';
import { exportQuery, type ExportFormat, type ExportMode } from '../../../services/tableExport';
import { useTableExport } from './useTableExport';
import { type TableCriteria } from './tableQuery';

type Change<T> = T | ((previous: T) => T);

export interface ServerTableProps<T extends { id: number }> {
  rows: T[];
  rowCount: number;
  isLoading: boolean;
  hasLoaded: boolean;
  error: ApiError | null;
  refresh: () => void;
  sorting: MRT_SortingState;
  columnVisibility: Record<string, boolean>;
  columnFilters: MRT_ColumnFiltersState;
  pagination: MRT_PaginationState;
  globalFilter: string;
  rowSelection: MRT_RowSelectionState;
  setRowSelection: (value: Change<MRT_RowSelectionState>) => void;
  showGlobalFilter: boolean;
  setShowGlobalFilter: (value: Change<boolean>) => void;
  handleRemoveAllFilters: () => void;
  setGlobalFilter: (value: Change<string>) => void;
  setSorting: (value: Change<MRT_SortingState>) => void;
  setColumnVisibility: (value: Change<Record<string, boolean>>) => void;
  setColumnFilters: (value: Change<MRT_ColumnFiltersState>) => void;
  setPagination: (value: Change<MRT_PaginationState>) => void;
  handleRefreshButton: () => void;
  openEditPage: (detail: { original: T }) => void;
  openCreatePage?: () => void;
}

interface TablePresentation<T extends { id: number }> extends ServerTableProps<T> {
  columns: MRT_ColumnDef<T>[];
  title: string;
  recordLabel: string;
  searchPlaceholder: string;
  createLabel?: string;
  extraActions?: ReactNode;
  dateFields?: string[];
  fieldAliases?: Record<string, string>;
  exportEndpoint?: string;
  exportParams?: (criteria: TableCriteria) => URLSearchParams;
  rowTestKey?: (row: T) => string;
}

export const inputStyle = {
  backgroundColor: 'transparent', borderRadius: 1.5, color: '#172b29',
  '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: 1.5 },
  '& .MuiInputBase-input': { color: '#172b29', fontSize: 14 },
  '& .MuiInputLabel-root': { color: '#fff', fontSize: 14, backgroundColor: 'transparent', transform: 'translate(12px, -12px) scale(0.75)' },
  '& .MuiSvgIcon-root, & .MuiIconButton-root': { color: '#374b48' },
  '&& .MuiOutlinedInput-root fieldset, && .MuiOutlinedInput-root:hover fieldset, && .MuiOutlinedInput-root.Mui-focused fieldset': { border: 0, backgroundColor: 'transparent' },
  '&& .MuiOutlinedInput-root.Mui-focused': { outline: 0, boxShadow: `0 0 0 2px ${colorConfigs.buttons.active}` },
  '&& .MuiInputBase-input:focus': { outline: 0, boxShadow: 'none' },
  '&& .MuiOutlinedInput-root.Mui-error': { outline: 0, boxShadow: '0 0 0 2px #b3261e' },
  '& .MuiFormHelperText-root.Mui-error': { color: '#b3261e', backgroundColor: '#fff', mx: 0, px: 0.5, whiteSpace: 'normal' },
};

const bannerStyle = {
  minHeight: 56, boxSizing: 'border-box', borderRadius: 1.5, px: 2, py: 1,
  alignItems: 'center', fontSize: 14,
  '& .MuiAlert-message': { py: 0, minWidth: 0 },
  '& .MuiAlert-icon': { py: 0 },
  '& .MuiAlert-action': { alignItems: 'center', pl: 2, py: 0, mr: 0 },
};
const bannerButtonStyle = { height: 32, whiteSpace: 'nowrap', boxShadow: 'none' };

/** Shared controls and styling; each table owns its columns, query mapping and actions. */
const ServerTable = <T extends { id: number },>(props: TablePresentation<T>) => {
  const { rows, rowSelection, setRowSelection, error, isLoading, hasLoaded } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [now, setNow] = useState(Date.now());
  const canExport = !!props.exportEndpoint && !!props.exportParams;
  const downloadQuery = props.exportParams ? exportQuery(props.exportParams({ sorting: props.sorting, columnFilters: props.columnFilters,
    globalFilter: props.globalFilter, pagination: props.pagination })) : "";
  const download = useTableExport(props.exportEndpoint ?? "", downloadQuery);
  useEffect(() => {
    if (download.error?.status === 401 || download.error?.status === 403) setRowSelection({});
  }, [download.error?.status, setRowSelection]);
  const retryAt = Math.max(error?.retryAt ?? 0, download.error?.retryAt ?? 0);
  useEffect(() => {
    setNow(Date.now());
    if (!retryAt || retryAt <= Date.now()) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= retryAt) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);
  const retrySeconds = Math.max(0, Math.ceil(((retryAt ?? 0) - now) / 1000));
  const actionsDisabled = isLoading || !!error || download.busy || retrySeconds > 0;
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const columns = props.columns;
  const dates = props.dateFields ?? [];
  const testKey = props.rowTestKey ?? ((row: T) => `id-${row.id}`);
  const fieldError = (column: string, rangeIndex?: number) => {
    const boundary = rangeIndex === undefined ? '' : rangeIndex === 0 ? '_from_date' : '_to_date';
    return error?.fields[(props.fieldAliases?.[column] ?? column) + boundary] ?? error?.fields[props.fieldAliases?.[column] ?? column];
  };
  const tableErrorMessage = error && `${error.message}${hasLoaded ? ' Previously loaded rows are shown.' : ''}${
    Object.entries(error.fields).filter(([key]) => !columns.some(column => key.startsWith(String(props.fieldAliases?.[String(column.accessorKey ?? column.id)] ?? column.accessorKey ?? column.id))))
      .map(([key, value]) => ` ${key}: ${value}`).join('')
  }`;

  const exportRows = (mode: ExportMode, format: ExportFormat) => {
    void download.run({ mode, format, selectedIds: Object.keys(rowSelection).filter(id => rowSelection[id]) });
    setAnchorEl(null);
  };

  const table = useMaterialReactTable({
    columns, data: rows, rowCount: props.rowCount,
    getRowId: row => String(row.id),
    localization: { filteringByColumn: 'Filtering by {column}' },
    manualFiltering: true, manualSorting: true, manualPagination: true,
    enableMultiSort: false, enableColumnFilterModes: false,
    enableColumnOrdering: true, enableRowSelection: canExport, selectAllMode: 'page',
    enableStickyHeader: true, enableStickyFooter: true,
    positionToolbarAlertBanner: 'none',
    defaultColumn: { minSize: 100, maxSize: 500 },
    initialState: { density: 'compact', showColumnFilters: true },
    displayColumnDefOptions: { 'mrt-row-select': { size: 60, minSize: 60, maxSize: 60, visibleInShowHideMenu: false } },
    state: {
      isLoading: isLoading && !hasLoaded, showProgressBars: isLoading && hasLoaded,
      sorting: props.sorting, columnFilters: props.columnFilters, pagination: props.pagination,
      globalFilter: props.globalFilter, showGlobalFilter: props.showGlobalFilter,
      columnVisibility: props.columnVisibility, rowSelection,
      showAlertBanner: !!error,
    },
    onSortingChange: props.setSorting, onColumnFiltersChange: props.setColumnFilters,
    onPaginationChange: props.setPagination, onGlobalFilterChange: props.setGlobalFilter,
    onShowGlobalFilterChange: props.setShowGlobalFilter,
    onColumnVisibilityChange: props.setColumnVisibility, onRowSelectionChange: props.setRowSelection,
    muiTablePaperProps: { elevation: 0, sx: { height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #d5dedc', borderRadius: 1.5, overflow: 'hidden' } },
    muiTableContainerProps: { sx: { flex: 1, minHeight: 0, overflow: 'auto', maxHeight: 'none' } },
    muiTopToolbarProps: { sx: { backgroundColor: '#fff', color: '#172b29', p: 1, minHeight: 60,
      '& > .MuiBox-root:not(.Mui-ToolbarDropZone)': { position: 'relative', alignItems: 'center', flexWrap: 'wrap', gap: 1 },
      '& .MuiTextField-root': { ...inputStyle, minWidth: { xs: 200, sm: 230 }, '& .MuiOutlinedInput-root': { backgroundColor: '#f4f8f7', borderRadius: 1.5, height: 40 } },
    } },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        ...(dates.includes(column.id) ? { '& .MuiCollapse-wrapperInner > .MuiBox-root': { gridTemplateColumns: '1fr', gap: 2.5 } } : {}),
        backgroundColor: colorConfigs.tables.headBg, color: '#fff', fontSize: 20, fontWeight: 700, py: 2,
        '& .MuiCollapse-root': { mt: dates.length > 0 ? 1.5 : 1, overflow: 'visible' },
        '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { overflow: 'visible' },
        '& .MuiIconButton-root, & .MuiSvgIcon-root': { color: '#fff' },
        '& .MuiInputBase-root .MuiIconButton-root, & .MuiInputBase-root .MuiSvgIcon-root, & .MuiFormControlLabel-root .MuiCheckbox-root, & .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root': { color: '#374b48' },
        '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active': { color: '#fff', ml: 0.5, mr: 0 },
        '&& .MuiTableSortLabel-root .MuiTableSortLabel-icon': { color: '#fff !important', opacity: 1, ml: '0 !important', mr: 0 },
        '& .MuiFormControlLabel-root': { backgroundColor: '#fff', borderRadius: 1.5, color: '#172b29', mx: 0, mt: 0, pr: 1, fontSize: 14 },
      },
    }),
    muiFilterTextFieldProps: ({ column, rangeFilterIndex }) => ({
      variant: 'outlined', size: 'small', sx: inputStyle,
      error: !!fieldError(column.id, rangeFilterIndex), helperText: fieldError(column.id, rangeFilterIndex),
    }),
    muiFilterDatePickerProps: ({ column, rangeFilterIndex }) => ({
      format: 'DD/MM/YYYY', label: rangeFilterIndex === 0 ? 'From' : 'To',
      slotProps: { textField: { placeholder: 'DD/MM/YYYY', InputLabelProps: { shrink: true }, sx: { ...inputStyle, '& .MuiInputLabel-root': { color: '#fff !important', fontSize: 14, lineHeight: 1.2, fontWeight: 500, backgroundColor: 'transparent', transform: 'translate(12px, -14px) scale(0.75)' } }, error: fieldError(column.id, rangeFilterIndex) ? true : undefined, helperText: fieldError(column.id, rangeFilterIndex) } },
    }),
    muiSearchTextFieldProps: { placeholder: props.searchPlaceholder, variant: 'outlined', size: 'small', error: !!error?.fields.search, helperText: error?.fields.search },
    renderBottomToolbar: ({ table }) => <TablePaginationControls recordLabel={props.recordLabel} count={props.rowCount}
      pageIndex={props.pagination.pageIndex} pageSize={props.pagination.pageSize}
      onPageChange={page => table.setPageIndex(page)} onPageSizeChange={size => table.setPageSize(size)} />,
    muiSelectAllCheckboxProps: { disabled: actionsDisabled, 'data-testid': 'Select-all-checkbox' } as any,
    muiSelectCheckboxProps: ({ row }) => ({ disabled: actionsDisabled, 'data-testid': `Select-row-with-${testKey(row.original)}` } as any),
    muiTableBodyRowProps: ({ row }) => ({
      'data-testid': `Row-with-${testKey(row.original)}`,
      onClick: event => {
        if (!(event.target as HTMLElement).closest('button, input, a, label')) props.openEditPage(row);
      },
      sx: { backgroundColor: row.index % 2 === 0 ? '#fff' : '#f4f8f7', cursor: 'pointer', '&:hover td': { backgroundColor: '#e8f3f0' } },
    }),
    muiTableBodyCellProps: ({ cell, row }) => ({
      'data-testid': `${cell.column.id}-cell-for-row-with-${testKey(row.original)}`,
      sx: { color: '#172b29', fontSize: 14, whiteSpace: 'nowrap', borderBottom: '1px solid #e1e8e6' },
    }),
    renderToolbarInternalActions: ({ table }) => <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <MRT_ToggleGlobalFilterButton table={table} sx={tableIconButtonStyle} aria-pressed={table.getState().showGlobalFilter} data-testid="Toggle-Search" />
      <MRT_ToggleFiltersButton table={table} sx={tableIconButtonStyle} aria-pressed={table.getState().showColumnFilters} data-testid="Toggle-Filters" />
      <MRT_ShowHideColumnsButton table={table} sx={tableIconButtonStyle} data-testid="Toggle-Columns" />
      <MRT_ToggleDensePaddingButton table={table} sx={tableIconButtonStyle} data-testid="Toggle-Density" />
      <MRT_ToggleFullScreenButton table={table} sx={tableIconButtonStyle} aria-pressed={table.getState().isFullScreen} data-testid="Toggle-Fullscreen" />
    </Box>,
    renderTopToolbar: ({ table }) => <Box sx={{ flexShrink: 0, backgroundColor: '#fff', pt: 1.5 }}>
      <Typography component="h1" align="center" fontWeight={700} fontSize={28} color={colorConfigs.tables.headBg} sx={{ mb: '-5px', px: 7 }}>{props.title}</Typography>
      {(error || download.error || selectedCount > 0) && <Box sx={{ mx: 2, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {error && <Alert severity="error" sx={bannerStyle} action={error.status !== 401 && error.status !== 403 &&
          <Button variant="outlined" color="inherit" size="small" sx={bannerButtonStyle} onClick={props.refresh} disabled={retrySeconds > 0}>
            {retrySeconds > 0 ? `Retry in ${retrySeconds}s` : 'Retry'}
          </Button>}>
          {tableErrorMessage}
        </Alert>}
        {download.error && <Alert severity="error" sx={bannerStyle} action={<Button variant="outlined" color="inherit" size="small" sx={bannerButtonStyle} disabled={actionsDisabled} onClick={() => void download.retry()}>Retry export{retrySeconds > 0 ? ` (${retrySeconds}s)` : ''}</Button>}>{download.error.message}</Alert>}
        {selectedCount > 0 && <Alert severity="info" role="status" aria-label="Table selection" icon={false} sx={bannerStyle}
          action={<Button variant="outlined" color="inherit" size="small" sx={bannerButtonStyle} onClick={() => props.setRowSelection({})}>Clear selection</Button>}>
          <Typography variant="body2" fontWeight={600}>{selectedCount.toLocaleString()} {selectedCount === 1 ? 'row' : 'rows'} selected</Typography>
          <Typography variant="caption">Selection clears when filters or search change.</Typography>
        </Alert>}
      </Box>}
      {download.message && <Typography role="status" variant="body2" sx={{ mx: 2, mt: 1 }}>{download.message}</Typography>}
      <MRT_TopToolbar table={table} />
    </Box>,
    renderTopToolbarCustomActions: () => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' },
      '& > .MuiButton-root, & > span > .MuiButton-root': { height: 40, boxSizing: 'border-box' },
    }}>
      {canExport && <Button variant="outlined" size="small" endIcon={<KeyboardArrowDownIcon />} disabled={actionsDisabled}
        data-testid="RareButton-iykl" onClick={event => setAnchorEl(event.currentTarget)}>{download.busy ? 'Exporting…' : 'Export'}</Button>}
      <Tooltip title={`Refresh ${props.recordLabel}`}><span><Button aria-label={`Refresh ${props.recordLabel}`} variant="outlined" size="small" disabled={isLoading || retrySeconds > 0}
        data-testid="RareButton-j1iz" onClick={props.handleRefreshButton} sx={tableIconButtonStyle}><RefreshIcon fontSize="small" /></Button></span></Tooltip>
      <Tooltip title="Clear all filters"><Button aria-label="Clear all filters" variant="outlined" size="small" data-testid="RareButton-gqid"
        onClick={props.handleRemoveAllFilters} sx={tableIconButtonStyle}><FilterAltOffOutlinedIcon fontSize="small" /></Button></Tooltip>
      {props.openCreatePage && <Button variant="contained" size="small" startIcon={<GroupAddOutlinedIcon />} data-testid="RareButton-xajw" onClick={props.openCreatePage}>{props.createLabel}</Button>}
      {props.extraActions}
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {(['selected', 'all_matching'] as const).flatMap(mode => (['csv', 'xlsx'] as const).map(format =>
          <MenuItem key={`${mode}-${format}`} disabled={actionsDisabled || (mode === 'selected' && selectedCount === 0)} onClick={() => exportRows(mode, format)}>
            Export {mode === 'selected' ? `selected (${selectedCount})` : 'all matching'} as {format.toUpperCase()}
          </MenuItem>))}
      </Menu>
    </Box>,
  });

  return <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb"><MaterialReactTable table={table} /></LocalizationProvider>;
};

export default ServerTable;
